import { LazyVideo } from "./LazyVideo";

type PageIntroVideo = {
  src: string;
  poster: string;
  ariaLabel: string;
};

interface PageIntroProps {
  eyebrow: string;
  title: string;
  description: string;
  bullets?: string[];
  backgroundVideo?: PageIntroVideo;
  image?: {
    src: string;
    alt: string;
  };
}

export function PageIntro({ eyebrow, title, description, bullets, backgroundVideo, image }: PageIntroProps) {
  return (
    <section className="relative overflow-hidden px-6 md:px-8 lg:px-12 pt-12 md:pt-20 pb-12 md:pb-16">
      {backgroundVideo && (
        <div className="absolute inset-0" aria-hidden="true">
          <LazyVideo
            src={backgroundVideo.src}
            poster={backgroundVideo.poster}
            ariaLabel={backgroundVideo.ariaLabel}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-background/35 max-md:bg-background/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/60 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-transparent to-background" />
          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-t from-transparent to-background" />
        </div>
      )}
      <div className={`relative z-10 mx-auto ${image ? "max-w-6xl grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-14" : "max-w-4xl"}`}>
        <div className={image ? "min-w-0" : undefined}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4">
          {eyebrow}
        </p>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight text-foreground">
          {title}
        </h1>
        <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
          {description}
        </p>

        {bullets && bullets.length > 0 && (
          <ul className="mt-8 flex flex-wrap gap-3">
            {bullets.map((bullet) => (
              <li
                key={bullet}
                className="rounded-full border border-border/60 px-4 py-2 text-sm text-muted-foreground"
              >
                {bullet}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href="https://calendly.com/cgould-mn/new-meeting"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
          >
            Schedule a Call
          </a>
          <a
            href="/"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-border/60 px-6 py-3 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            See all services
          </a>
        </div>
        </div>
        {image && (
          <div className="min-w-0 max-md:mt-2 lg:self-end -mb-12 md:-mb-16">
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-auto block lg:origin-bottom lg:scale-[1.18]"
              loading="eager"
            />
          </div>
        )}
      </div>
    </section>
  );
}
